import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { PageHeader, Button, Badge } from "@/components/ui/PageHeader";
import { useAuth } from "@/context/AuthContext";
import {
  getMyHousehold,
  createHousehold,
  inviteMember,
  respondToInvite,
  removeMember,
  renameHousehold,
  handOverHousehold,
  dissolveHousehold,
  createInviteLink,
  revokeInviteLink,
  withdrawHouseholdInvite,
} from "@/api/households";
import { getParichay } from "@/api/users";

/**
 * Household manage screen — create, rename, invite (by userId; the Lab is a
 * test surface), share or revoke a join link, accept/decline, remove/leave,
 * hand over, dissolve. Each button shows only when the server's
 * `allowedActions` offers it, as a client must. Declines are silent on the inviter's side by design;
 * the Lab makes that observable by simply not showing anything anywhere else.
 */
export default function HouseholdsLab() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const [name, setName] = useState("");
  const [inviteeId, setInviteeId] = useState("");
  const [newName, setNewName] = useState("");
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["my-household"],
    queryFn: getMyHousehold,
  });

  const my = data?.data;
  const isOwner = my?.myRole === "owner";
  const can = (action: string) => my?.allowedActions?.includes(action) ?? false;
  // The open link rides on the household for the owner, so the page shows it
  // without making one first.
  const link = my?.household?.inviteLink?.url ?? null;

  // What another member's view of my Parichay says about my household.
  const parichay = useQuery({
    queryKey: ["parichay", user?.id],
    queryFn: () => getParichay(user!.id),
    enabled: Boolean(user?.id),
  });
  const publicHousehold = parichay.data?.data.publicHousehold ?? null;

  function invalidate() {
    qc.invalidateQueries({ queryKey: ["my-household"] });
    qc.invalidateQueries({ queryKey: ["parichay"] });
  }

  async function run(action: () => Promise<unknown>) {
    setBusy(true);
    setFeedback(null);
    try {
      await action();
      invalidate();
    } catch (e) {
      setFeedback(e instanceof Error ? e.message : "Action failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Household"
        subtitle="The retention unit — one household per user. Invites are pull, never force: nothing joins until the invitee accepts, and declines are silent."
        breadcrumbs={[{ label: "Dashboard", href: "/" }, { label: "Household" }]}
      />

      {feedback && <p className="text-sm text-red-600">{feedback}</p>}

      {isLoading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : !my ? null : (
        <>
          {/* Pending invites — visible housed or not */}
          {my.pendingInvites.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-2">
              <h3 className="text-sm font-medium text-amber-900">
                Invites waiting for you
              </h3>
              {my.pendingInvites.map((inv) => (
                <div key={inv.id} className="flex items-center justify-between gap-2">
                  <span className="text-sm text-amber-800">
                    <span className="font-medium">{inv.invitedBy.name}</span>
                    {" → "}
                    {inv.household.name ?? "their household"}
                    <span className="text-xs text-amber-600">
                      {" "}
                      (expires {new Date(inv.expiresAt).toLocaleDateString()})
                    </span>
                  </span>
                  <span className="flex gap-1">
                    <Button
                      size="sm"
                      onClick={() => run(() => respondToInvite(inv.id, "accept"))}
                      disabled={busy}
                    >
                      Accept
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => run(() => respondToInvite(inv.id, "decline"))}
                      disabled={busy}
                    >
                      Decline
                    </Button>
                  </span>
                </div>
              ))}
            </div>
          )}

          <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-2">
            <h3 className="text-sm font-medium text-gray-900">What other members see on your Parichay</h3>
            {publicHousehold ? (
              <p className="text-sm text-gray-900">"{publicHousehold.label}"</p>
            ) : (
              <p className="text-sm text-gray-500">
                Nothing about your household.{" "}
                <Link to="/lab/settings" className="text-blue-600 underline">
                  Turn it on in Settings
                </Link>{" "}
                to show one fixed line. It never shows the name, the size or the members.
              </p>
            )}
            <p className="text-xs text-gray-500">
              The server allows you: {my.allowedActions.length > 0 ? my.allowedActions.join(", ") : "nothing"}
            </p>
          </div>

          {my.household === null ? (
            /* Unhoused — create */
            <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-3">
              <h3 className="text-sm font-medium text-gray-900">
                You're not in a household yet
              </h3>
              <div className="flex gap-2">
                <input
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                  placeholder='Household name (optional, e.g. "The Sharmas")'
                  value={name}
                  maxLength={100}
                  onChange={(e) => setName(e.target.value)}
                />
                <Button
                  onClick={() => run(() => createHousehold(name.trim() || undefined))}
                  disabled={busy || !can("create")}
                >
                  Create household
                </Button>
              </div>
            </div>
          ) : (
            /* Housed — members + owner tools */
            <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-4">
              <div className="flex items-center gap-2">
                <h3 className="font-medium text-gray-900">
                  {my.household.name ?? "My household"}
                </h3>
                <Badge variant="gray">
                  {my.household.members.length}/8 members
                </Badge>
              </div>

              <ul className="space-y-2">
                {my.household.members.map((m) => (
                  <li key={m.userId} className="flex items-center justify-between gap-2">
                    <span className="text-sm text-gray-900">
                      {m.user.name}
                      {m.userId === user?.id && (
                        <span className="text-gray-400"> (you)</span>
                      )}{" "}
                      <Badge variant={m.role === "owner" ? "purple" : "gray"}>
                        {m.role}
                      </Badge>
                      <span className="block text-xs text-gray-500">
                        {[m.user.city, m.user.state].filter(Boolean).join(", ") || "No city given"}
                        {" · "}
                        {m.user.roots.length > 0
                          ? `From ${m.user.roots.map((r) => r.label).join(", ")}`
                          : "No roots given"}
                      </span>
                    </span>
                    <span className="flex gap-1">
                      {m.userId !== user?.id && can("hand_over") && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            if (window.confirm(`Hand the household to ${m.user.name}? They are the owner at once, and you become a member.`)) {
                              void run(() => handOverHousehold(m.userId));
                            }
                          }}
                          disabled={busy}
                        >
                          Hand over
                        </Button>
                      )}
                      {((m.userId !== user?.id && can("remove_member")) ||
                        (m.userId === user?.id && can("leave"))) && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => run(() => removeMember(m.userId))}
                          disabled={busy}
                        >
                          {m.userId === user?.id ? "Leave" : "Remove"}
                        </Button>
                      )}
                    </span>
                  </li>
                ))}
              </ul>

              {isOwner && (
                <>
                  {can("rename") && <div className="flex gap-2 pt-2 border-t border-gray-100">
                    <input
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                      placeholder="A new name for the household"
                      value={newName}
                      maxLength={100}
                      onChange={(e) => setNewName(e.target.value)}
                    />
                    <Button
                      size="sm"
                      onClick={() =>
                        run(async () => {
                          await renameHousehold(newName.trim());
                          setNewName("");
                        })
                      }
                      disabled={busy || !newName.trim()}
                    >
                      Rename
                    </Button>
                  </div>}
                  {can("invite") && <div className="flex gap-2 pt-2 border-t border-gray-100">
                    <input
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm font-mono"
                      placeholder="Invite by userId (UUID)"
                      value={inviteeId}
                      onChange={(e) => setInviteeId(e.target.value)}
                    />
                    <Button
                      size="sm"
                      onClick={() =>
                        run(async () => {
                          await inviteMember(inviteeId.trim());
                          setInviteeId("");
                        })
                      }
                      disabled={busy || !inviteeId.trim()}
                    >
                      Invite
                    </Button>
                  </div>}
                  {can("withdraw_invite") && my.sentInvites && my.sentInvites.length > 0 && (
                    <ul className="space-y-1 pt-2 border-t border-gray-100">
                      {my.sentInvites.map((inv) => (
                        <li key={inv.id} className="flex items-center gap-2 text-xs text-gray-700">
                          <span>
                            {inv.invitee.name} has not answered yet
                          </span>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => run(() => withdrawHouseholdInvite(inv.id))}
                            disabled={busy}
                          >
                            Withdraw
                          </Button>
                        </li>
                      ))}
                    </ul>
                  )}
                  {(can("share_invite_link") || can("revoke_invite_link")) && (
                    <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-gray-100">
                      {can("share_invite_link") && (
                        <Button
                          size="sm"
                          onClick={() => run(() => createInviteLink())}
                          disabled={busy}
                        >
                          Share a join link
                        </Button>
                      )}
                      {can("revoke_invite_link") && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => run(() => revokeInviteLink())}
                          disabled={busy}
                        >
                          Revoke the link
                        </Button>
                      )}
                      {link && <code className="text-xs break-all">{link}</code>}
                    </div>
                  )}
                  {can("dissolve") && <div className="pt-2 border-t border-gray-100">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        if (window.confirm("Dissolve the household? Members and invites are removed.")) {
                          void run(() => dissolveHousehold());
                        }
                      }}
                      disabled={busy}
                    >
                      Dissolve household
                    </Button>
                  </div>}
                </>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
